import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogModule,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { TablerIconsModule } from 'angular-tabler-icons';

export interface ManualReminderDeliveryFailureRow {
  email: string;
  reason: string;
}

export interface ManualReminderDeliveryResultDialogData {
  reminderLevel: number;
  deliverySummary: 'all_failed' | 'partial_success';
  failures: ManualReminderDeliveryFailureRow[];
}

@Component({
  selector: 'app-manual-reminder-delivery-result-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogActions,
    MatDialogClose,
    MatDialogTitle,
    MatDialogContent,
    MatDialogModule,
    TablerIconsModule
  ],
  template: `
    <div class="header">
      <h5 mat-dialog-title>WARNING</h5>
    </div>

    <div mat-dialog-content class="content">
      <div class="lead-card">
        <i-tabler name="alert-triangle" class="icon-16"></i-tabler>
        <span>{{ introMessage }}</span>
      </div>

      <div class="failures-block">
        <div class="failures-title">
          <i-tabler name="mail-off" class="icon-16"></i-tabler>
          <span>Failure Details</span>
        </div>

        <div *ngIf="data.failures.length > 0; else noFailureDetails" class="failures-grid">
          <div class="grid-head">Contact</div>
          <div class="grid-head">Reason</div>

          <ng-container *ngFor="let failure of data.failures">
            <div class="grid-cell contact-cell">{{ failure.email }}</div>
            <div class="grid-cell reason-cell">{{ failure.reason }}</div>
          </ng-container>
        </div>

        <ng-template #noFailureDetails>
          <div class="no-details">No failure details available.</div>
        </ng-template>
      </div>
    </div>

    <div mat-dialog-actions class="actions">
      <button mat-flat-button class="cancel-btn" [mat-dialog-close]="false">Close</button>
      <button mat-flat-button class="confirm-btn" [mat-dialog-close]="true" cdkFocusInitial>OK</button>
    </div>
  `,
  styles: [`
    .header {
      padding: 20px 24px 10px;
    }
    .content {
      padding: 0 24px 10px !important;
    }
    .lead-card {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 12px;
      border: 1px solid #efcb7d;
      background: linear-gradient(180deg, #fff8dd 0%, #fff2c7 100%);
      border-radius: 10px;
      color: #6f5200;
      margin-bottom: 12px;
      font-size: 13px;
      line-height: 1.35;
    }
    .failures-block {
      border: 1px solid #e6ebf1;
      border-radius: 10px;
      overflow: hidden;
    }
    .failures-title {
      display: flex;
      align-items: center;
      gap: 8px;
      font-weight: 600;
      padding: 10px 12px;
      background: #f7f9fc;
      border-bottom: 1px solid #e6ebf1;
      color: #2a3547;
    }
    .failures-grid {
      display: grid;
      grid-template-columns: 40% 60%;
    }
    .grid-head {
      padding: 10px 12px;
      font-weight: 600;
      color: #2a3547;
      border-bottom: 1px solid #e6ebf1;
      background: #fafbfd;
    }
    .grid-cell {
      padding: 10px 12px;
      border-bottom: 1px solid #f0f3f8;
      font-size: 13px;
      line-height: 1.35;
    }
    .contact-cell {
      font-weight: 600;
      color: #2a3547;
      word-break: break-word;
    }
    .reason-cell {
      color: #4f5b6b;
      word-break: break-word;
    }
    .failures-grid .grid-cell:nth-last-child(-n+2) {
      border-bottom: 0;
    }
    .no-details {
      color: #4f5b6b;
      font-size: 13px;
      padding: 12px;
      background: #fff;
    }
    .actions {
      padding: 0 24px 24px !important;
      display: flex;
      gap: 10px;
      justify-content: flex-end;
    }
    .cancel-btn {
      background: #ff6692;
      color: #fff;
    }
    .confirm-btn {
      background: #5d87ff;
      color: #fff;
    }
  `]
})
export class ManualReminderDeliveryResultDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<ManualReminderDeliveryResultDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ManualReminderDeliveryResultDialogData
  ) {}

  get introMessage(): string {
    if (this.data.deliverySummary === 'all_failed') {
      return `No billing contact received reminder level ${this.data.reminderLevel}. The level was not updated.`;
    }
    return `Reminder level ${this.data.reminderLevel} was validated, but some recipients failed.`;
  }
}

